import { Modal, Typography } from '@smartgift/frontend-toolkit';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EditModalContext } from '@hero/components/organisms/ContactsEditModal/context';
import { NavigationSecondaryContent } from '@hero/components/organisms/ContactsEditModal/NavigationSecondaryContent';
import { formatCurrency } from '@hero/utils';

import { ReviewAndSubmit } from '../ReviewAndSubmit';

function WithoutContactReviewAndSubmitModal({
  isOpen,
  onRequestClose,
  onSave,
  contactCount,
  newContactCount,
  newEstimatedCharge,
  isCCPayment
}) {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const table = useMemo(() => {
    const addedRecipient = {
      key: t(newContactCount - contactCount > 0 ? 'addedRecipients' : 'removedRecipients'),
      value: Math.round(newContactCount - contactCount),
      isActive: true
    };
    const newRecipientTotal = {
      key: t('newRecipientTotal'),
      value: newContactCount
    };

    const newTable = [addedRecipient, newRecipientTotal];

    const minEstimatedCharge = newEstimatedCharge?.new?.serviceCharges?.min;
    const maxEstimatedCharge = newEstimatedCharge?.new?.serviceCharges?.max;
    const shouldShowEstimatedChargeRange = minEstimatedCharge === 0 && maxEstimatedCharge !== 0;

    if (newEstimatedCharge) {
      newTable.push({
        key: t('additionalCharge'),
        type: 'accordion',
        value: formatCurrency(newEstimatedCharge?.difference?.total),
        table: [
          {
            key: t('items'),
            helpText: `(${t('costSummarySubTitle1', {
              recipientCount: newEstimatedCharge?.difference?.recipientCount
            })})`,
            value: formatCurrency(newEstimatedCharge?.difference?.merchandise)
          },
          {
            key: t('estimatedTax'),
            value: formatCurrency(newEstimatedCharge?.difference?.estimatedTax)
          },
          {
            key: (
              <>
                {t('estimatedServiceCharge')}
                {shouldShowEstimatedChargeRange && (
                  <Typography variant="caption1" color="fg" emphasis="weak">
                    {t('serviceChargeMessageForFreeShip')}
                  </Typography>
                )}
              </>
            ),
            value: shouldShowEstimatedChargeRange ? (
              <>
                {formatCurrency(minEstimatedCharge)} - {formatCurrency(maxEstimatedCharge)}
              </>
            ) : (
              formatCurrency(newEstimatedCharge?.difference?.serviceCharge)
            )
          }
        ]
      });
    }

    return newTable;
  }, [newEstimatedCharge]);

  return (
    <EditModalContext.Provider value={newEstimatedCharge}>
      <Modal
        size="xl"
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        title={t('reviewAndConfirm')}
        components={{ SecondaryContent: NavigationSecondaryContent }}
        primaryButtonProps={{
          onClick: async () => {
            setIsLoading(true);
            try {
              await onSave();
            } finally {
              setIsLoading(false);
            }
          },
          children: t(isCCPayment ? 'payAndSubmit' : 'submit'),
          disabled: isLoading,
          loading: isLoading
        }}
      >
        <ReviewAndSubmit icon="contacts" desc={t('reviewAndConfirmDescription')} table={table} />
      </Modal>
    </EditModalContext.Provider>
  );
}

export { WithoutContactReviewAndSubmitModal };
